import { useEffect, useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Users, Calculator, Layers, Cpu, Shield, AlertTriangle, Sparkles, LayoutDashboard,
  ChevronRight, Search, Shield as ShieldIcon, Dot, BarChart3,
  TrendingUp, ShoppingBag, Megaphone, ShoppingCart, Factory, Truck, Settings, FolderKanban, Building2, Scale, Crown, Server, IdCard, FileText, PenLine, Brain, Briefcase, GitBranch, ClipboardList, Target, Clock as ClockIcon, HeartPulse,
} from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useI18n } from "../../context/I18nContext";
import { locStudio } from "../../pages/os/catalogI18n";

const SUITE_ICONS = {
  people: Users, finance: Calculator, design: Layers, it: Cpu, security: Shield, risk: AlertTriangle, data_ai: Sparkles,
  sales: TrendingUp, commerce: ShoppingBag, marketing: Megaphone, procurement: ShoppingCart, manufacturing: Factory,
  supply_chain: Truck, operations: Settings, projects: FolderKanban, assets: Building2, legal: Scale, executive: Crown, platform: Server,
};

const loadJSON = (k) => { try { return JSON.parse(localStorage.getItem(k) || "{}"); } catch { return {}; } };

export default function CatalogNav({ onNavigate }) {
  const { lang } = useI18n();
  const { user } = useAuth();
  const restricted = user?.role === "usuario_rd";
  const location = useLocation();
  const [data, setData] = useState(null);
  const [openSuites, setOpenSuites] = useState(() => loadJSON("omnia_nav_suites"));
  const [openStudios, setOpenStudios] = useState(() => loadJSON("omnia_nav_studios"));
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/catalog").then((r) => setData(r.data)).catch(() => {}); }, []);
  useEffect(() => { localStorage.setItem("omnia_nav_suites", JSON.stringify(openSuites)); }, [openSuites]);
  useEffect(() => { localStorage.setItem("omnia_nav_studios", JSON.stringify(openStudios)); }, [openStudios]);

  const appHref = (app) => app.route || `/os/app/${app.id}`;
  const isActive = (href) => location.pathname === href;

  const filtered = useMemo(() => {
    if (!data) return null;
    if (!q.trim()) return data;
    const term = q.toLowerCase();
    const suites = data.suites.map((s) => ({
      ...s,
      studios: s.studios.map((st) => ({ ...st, apps: st.apps.filter((a) => a.name.toLowerCase().includes(term)) }))
        .filter((st) => st.apps.length || st.name.toLowerCase().includes(term)),
    })).filter((s) => s.studios.length || s.name.toLowerCase().includes(term));
    return { ...data, suites };
  }, [data, q]);

  const searching = q.trim().length > 0;

  return (
    <div className="flex flex-col h-full" data-testid="catalog-nav">
      <div className="px-3 pb-3">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-cream/40" />
          <input data-testid="catalog-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar app…"
            className="w-full bg-cream/5 border border-cream/10 rounded-lg pl-8 pr-3 py-2 text-sm text-cream placeholder:text-cream/30 outline-none focus:border-violet" />
        </div>
      </div>
      <nav className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-3 pb-6 space-y-1">
        <Link to="/os" onClick={onNavigate} data-testid="catalog-nav-hub"
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${location.pathname === "/os" ? "bg-gradient-violet text-white" : "text-cream/70 hover:text-cream hover:bg-cream/5"}`}>
          <LayoutDashboard className="w-4 h-4" /> OMNIA Hub
        </Link>
        {!restricted && (<>
        <Link to="/os/directory" onClick={onNavigate} data-testid="catalog-nav-directory"
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${location.pathname === "/os/directory" ? "bg-gradient-violet text-white" : "text-cream/70 hover:text-cream hover:bg-cream/5"}`}>
          <Users className="w-4 h-4" /> OMNIA Directory
        </Link>
        <Link to="/os/user" onClick={onNavigate} data-testid="catalog-nav-my-profile"
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${["/os/user", "/os/mi-perfil", "/os/expediente"].includes(location.pathname) ? "bg-gradient-violet text-white" : "text-cream/70 hover:text-cream hover:bg-cream/5"}`}>
          <IdCard className="w-4 h-4" /> OMNIA User
        </Link>
        <Link to="/os/lider" onClick={onNavigate} data-testid="catalog-nav-leader"
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${location.pathname === "/os/lider" ? "bg-gradient-violet text-white" : "text-cream/70 hover:text-cream hover:bg-cream/5"}`}>
          <Crown className="w-4 h-4" /> OMNIA Leaderboard
        </Link>
        <Link to="/os/sign" onClick={onNavigate} data-testid="catalog-nav-sign"
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${location.pathname === "/os/sign" ? "bg-gradient-violet text-white" : "text-cream/70 hover:text-cream hover:bg-cream/5"}`}>
          <PenLine className="w-4 h-4" /> OMNIA Sign
        </Link>
        <Link to="/os/mi-panel" onClick={onNavigate} data-testid="catalog-nav-personal-dashboard"
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${location.pathname === "/os/mi-panel" ? "bg-gradient-violet text-white" : "text-cream/70 hover:text-cream hover:bg-cream/5"}`}>
          <BarChart3 className="w-4 h-4" /> OMNIA Personal Dashboard
        </Link>
        <Link to="/os/intelligence" onClick={onNavigate} data-testid="catalog-nav-financial"
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${location.pathname === "/os/intelligence" ? "bg-gradient-violet text-white" : "text-cream/70 hover:text-cream hover:bg-cream/5"}`}>
          <TrendingUp className="w-4 h-4" /> OMNIA Financial
        </Link>
        <Link to="/os/facturacion" onClick={onNavigate} data-testid="catalog-nav-facturacion"
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${location.pathname === "/os/facturacion" ? "bg-gradient-violet text-white" : "text-cream/70 hover:text-cream hover:bg-cream/5"}`}>
          <FileText className="w-4 h-4" /> OMNIA Facturación
        </Link>
        </>)}

        {restricted ? (
          [["/os/cuadrillas", "OMNIA Cuadrillas"], ["/os/correctivos", "OMNIA Correctivos"], ["/os/control-tickets", "OMNIA Tickets"], ["/os/flotilla", "OMNIA Flotilla (Strix)"], ["/os/planning", "OMNIA Planning"], ["/os/sheets", "OMNIA Documents Hub"]].map(([href, name]) => (
            <Link key={href} to={href} onClick={onNavigate} data-testid={`catalog-rd-${href.split("/").pop()}`}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive(href) ? "bg-gradient-violet text-white" : "text-cream/70 hover:text-cream hover:bg-cream/5"}`}>
              <Dot className="w-4 h-4 shrink-0 opacity-60" />
              <span className="truncate">{name}</span>
            </Link>
          ))
        ) : !filtered ? (
          <div className="px-3 py-4 text-cream/30 text-xs">Cargando…</div>
        ) : filtered.suites.map((suite) => {
          const SIcon = SUITE_ICONS[suite.id] || Layers;
          const open = searching || openSuites[suite.id];
          return (
            <div key={suite.id} data-testid={`catalog-suite-${suite.id}`}>
              <button onClick={() => setOpenSuites((p) => ({ ...p, [suite.id]: !p[suite.id] }))}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-cream/80 hover:bg-cream/5 transition-colors">
                <SIcon className="w-4 h-4 text-violet-300" />
                <span className="flex-1 text-left font-medium truncate">{suite.name}</span>
                <ChevronRight className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-90" : ""}`} />
              </button>
              {open && (
                <div className="ml-3 pl-2 border-l border-cream/10 space-y-0.5 mt-0.5">
                  {suite.studios.map((st) => {
                    const skey = `${suite.id}.${st.id}`;
                    const sopen = searching || openStudios[skey];
                    return (
                      <div key={st.id}>
                        <button onClick={() => setOpenStudios((p) => ({ ...p, [skey]: !p[skey] }))}
                          data-testid={`catalog-studio-${st.id}`}
                          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] text-cream/60 hover:text-cream hover:bg-cream/5 transition-colors">
                          <span className="flex-1 text-left truncate">{locStudio(st.name, lang)}</span>
                          <ChevronRight className={`w-3 h-3 transition-transform ${sopen ? "rotate-90" : ""}`} />
                        </button>
                        {sopen && (
                          <div className="ml-2 space-y-0.5 mt-0.5">
                            <Link to={`/os/studio/${st.id}`} onClick={onNavigate} data-testid={`catalog-studio-panel-${st.id}`}
                              className={`flex items-center gap-1.5 pl-4 pr-3 py-1.5 rounded-lg text-[13px] transition-colors ${isActive(`/os/studio/${st.id}`) ? "bg-gradient-violet text-white" : "text-violet-300/80 hover:text-violet-200 hover:bg-cream/5"}`}>
                              <BarChart3 className="w-3.5 h-3.5 shrink-0" />
                              <span className="truncate">Panel del Studio</span>
                            </Link>
                            {st.apps.map((app) => {
                              const href = appHref(app);
                              return (
                                <Link key={app.id} to={href} onClick={onNavigate} data-testid={`catalog-app-${app.id}`}
                                  className={`flex items-center gap-1.5 pl-4 pr-3 py-1.5 rounded-lg text-[13px] transition-colors ${isActive(href) ? "bg-gradient-violet text-white" : "text-cream/50 hover:text-cream hover:bg-cream/5"}`}>
                                  <Dot className="w-3.5 h-3.5 shrink-0 opacity-60" />
                                  <span className="truncate">{app.name}</span>
                                  {app.route ? null : <span className="ml-auto text-[9px] text-cream/25">app</span>}
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {filtered?.admin_apps?.length > 0 && (
          <div className="pt-4 mt-3 border-t border-cream/10">
            <div className="px-3 overline text-cream/30 mb-2 flex items-center gap-1.5"><ShieldIcon className="w-3 h-3" /> Admin</div>
            {filtered.admin_apps.map((app) => (
              <Link key={app.id} to={app.route} onClick={onNavigate} data-testid={`catalog-admin-${app.id}`}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] transition-colors ${isActive(app.route) ? "bg-gradient-violet text-white" : "text-cream/50 hover:text-cream hover:bg-cream/5"}`}>
                <Dot className="w-3.5 h-3.5 opacity-60" /> {app.name}
              </Link>
            ))}
          </div>
        )}
      </nav>
    </div>
  );
}
